import { Link, useLocation } from "react-router-dom";
import { 
  Menu, 
  X, 
  ChevronDown, 
  Bot, 
  Building2, 
  Network, 
  Briefcase, 
  Cpu,
  Clock,
  TrendingUp,
  Sparkles,
  Lightbulb,
  GitCompare,
  FileText,
  BarChart3,
  Newspaper,
  Mail,
  Heart,
  Search,
  CalendarDays,
  Store
} from "lucide-react";
import { useState } from "react";
import { Brand } from "@/components/Brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchCommand, useSearchCommand } from "@/components/SearchCommand";
const coreNavigation = [
  { name: "Robots", href: "/robots", icon: Bot, description: "Browse humanoid catalog" },
  { name: "Companies", href: "/companies", icon: Building2, description: "Explore manufacturers" },
  { name: "Skill Marketplace", href: "/marketplace", icon: Store, description: "Deploy humanoid skills" },
];

const intelligenceNavigation = [
  { name: "Timeline", href: "/timeline", icon: Clock, description: "Evolution of robots" },
  { name: "Ecosystem", href: "/ecosystem", icon: Network, description: "Market map & players" },
  { name: "Use Cases", href: "/use-cases", icon: Briefcase, description: "Applications & industries" },
  { name: "Technology", href: "/technology", icon: Cpu, description: "Tech stack & components" },
  { name: "Market Snapshot", href: "/market-snapshot", icon: TrendingUp, description: "Trends & projections" },
];

const opportunityNavigation = [
  { name: "Robo Recommender", href: "/recommend", icon: Sparkles, description: "AI-powered robot matching" },
  { name: "Opportunities", href: "/opportunities", icon: Lightbulb, description: "Strategic white spaces" },
  { name: "Compare", href: "/compare", icon: GitCompare, description: "Side-by-side analysis" },
];

const insightsNavigation = [
  { name: "News", href: "/news", icon: Newspaper, description: "Industry news feed" },
  { name: "Events", href: "/events", icon: CalendarDays, description: "Conferences & meetups" },
  { name: "Insights", href: "/insights", icon: BarChart3, description: "Market analysis" },
  { name: "Reports", href: "/reports", icon: FileText, description: "Premium research" },
];

export function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { open: searchOpen, setOpen: setSearchOpen } = useSearchCommand();

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + "/");
  const isGroupActive = (items: typeof coreNavigation) => items.some(item => isActive(item.href));

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <nav className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-8">
        <Brand size="md" showTagline={true} />

        <div className="hidden lg:flex items-center gap-0.5">
          {/* Core - Direct links */}
          {coreNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-md ${
                isActive(item.href)
                  ? "text-foreground bg-muted"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {item.name}
            </Link>
          ))}

          {/* Intelligence Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isGroupActive(intelligenceNavigation)
                    ? "text-foreground bg-muted"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                Intelligence
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 bg-popover border border-border shadow-lg">
              {intelligenceNavigation.map((item) => (
                <DropdownMenuItem key={item.name} asChild>
                  <Link
                    to={item.href}
                    className={`flex flex-col gap-0.5 px-3 py-2 cursor-pointer ${
                      isActive(item.href) ? "bg-muted" : ""
                    }`}
                  >
                    <span className="font-medium text-foreground">{item.name}</span>
                    <span className="text-xs text-muted-foreground">{item.description}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Opportunity Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isGroupActive(opportunityNavigation)
                    ? "text-foreground bg-muted"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                Opportunity
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 bg-popover border border-border shadow-lg">
              {opportunityNavigation.map((item) => (
                <DropdownMenuItem key={item.name} asChild>
                  <Link
                    to={item.href}
                    className={`flex flex-col gap-0.5 px-3 py-2 cursor-pointer ${
                      isActive(item.href) ? "bg-muted" : ""
                    }`}
                  >
                    <span className="font-medium text-foreground">{item.name}</span>
                    <span className="text-xs text-muted-foreground">{item.description}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Insights Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isGroupActive(insightsNavigation)
                    ? "text-foreground bg-muted"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                Insights
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 bg-popover border border-border shadow-lg">
              {insightsNavigation.map((item) => (
                <DropdownMenuItem key={item.name} asChild>
                  <Link
                    to={item.href}
                    className={`flex flex-col gap-0.5 px-3 py-2 cursor-pointer ${
                      isActive(item.href) ? "bg-muted" : ""
                    }`}
                  >
                    <span className="font-medium text-foreground">{item.name}</span>
                    <span className="text-xs text-muted-foreground">{item.description}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="w-px h-5 bg-border mx-2" />
          
          {/* Search button */}
          <button
            onClick={() => setSearchOpen(true)}
            className="px-2.5 py-1.5 text-sm transition-colors rounded-md flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <Search className="h-4 w-4" />
            <kbd className="hidden xl:inline-flex h-5 select-none items-center gap-0.5 rounded border border-border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
              ⌘K
            </kbd>
          </button>

          <div className="w-px h-5 bg-border mx-2" />
          
          {/* Contribute link */}
          <Link
            to="/contribute"
            className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-md ${
              isActive("/contribute")
                ? "text-primary bg-primary/10"
                : "text-primary hover:bg-primary/5"
            }`}
          >
            Contribute
          </Link>
        </div>

        {/* Mobile buttons */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
          <button
            className="p-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div 
        className={`lg:hidden border-t border-border bg-background overflow-hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container mx-auto py-4 px-4 space-y-1 overflow-y-auto max-h-[calc(80vh-2rem)]">
          {/* Core */}
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-2">
            Catalog
          </div>
          {coreNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </Link>
          ))}

          {/* Intelligence */}
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-2 mt-4">
            Intelligence
          </div>
          {intelligenceNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </Link>
          ))}

          {/* Opportunity */}
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-2 mt-4">
            Opportunity
          </div>
          {opportunityNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </Link>
          ))}

          {/* Insights */}
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-2 mt-4">
            Insights
          </div>
          {insightsNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </Link>
          ))}

          {/* Community */}
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-2 mt-4">
            Community
          </div>
          <Link
            to="/contribute"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isActive("/contribute")
                ? "bg-primary/10 text-primary"
                : "text-primary hover:bg-primary/5"
            }`}
          >
            <Heart className="h-4 w-4" />
            Contribute
          </Link>
          <Link
            to="/newsletter"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isActive("/newsletter")
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            <Mail className="h-4 w-4" />
            Newsletter
          </Link>
        </div>
      </div>

      {/* Search Command Palette */}
      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
